import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, FileSearch, Trophy } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

type Sol = {
  id: string; folio: string; fecha: string; estado: string;
  descripcion: string; fecha_limite: string; criterio_evaluacion: string;
  requisicion_folio?: string; proveedor_ganador_nombre?: string;
  n_cotizaciones: number;
};

export function CotizacionesPage() {
  const [estado, setEstado] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['cot-solicitudes', estado],
    queryFn: () => {
      const p = new URLSearchParams();
      if (estado) p.set('estado', estado);
      return api<Sol[]>(`/cotizaciones/solicitudes?${p}`);
    },
  });
  const items = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Adquisiciones · Cotizaciones</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Solicitudes de cotización</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Matriz comparativa de ofertas por proveedor (Art. 42 LAASSP).
          </p>
        </div>
        <Button onClick={() => setOpenNew(true)}><Plus size={16} /> Nueva solicitud</Button>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[12px] font-medium w-fit">
        {['', 'abierta', 'cerrada', 'adjudicada', 'desierta', 'cancelada'].map(e => (
          <button key={e || 'todas'} onClick={() => setEstado(e)}
            className={cn('rounded px-3 py-1.5 capitalize', estado === e ? 'bg-brand-500 text-white' : 'text-ink-mute hover:bg-muted')}>
            {e || 'todas'}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <FileSearch className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin solicitudes de cotización.</p>
            </div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Folio</th>
                  <th className="px-5 py-3">Fecha</th>
                  <th className="px-5 py-3">Descripción</th>
                  <th className="px-5 py-3">Requisición</th>
                  <th className="px-5 py-3 text-center">Cotizaciones</th>
                  <th className="px-5 py-3">Ganador</th>
                  <th className="px-5 py-3">Estado</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(s => (
                  <tr key={s.id} className="hover:bg-muted/50">
                    <td className="num px-5 py-3 font-semibold">{s.folio}</td>
                    <td className="num px-5 py-3 text-ink-mute">{fmt.date(s.fecha)}</td>
                    <td className="px-5 py-3 max-w-[280px] truncate">{s.descripcion}</td>
                    <td className="num px-5 py-3 text-ink-mute">{s.requisicion_folio ?? '—'}</td>
                    <td className="num px-5 py-3 text-center">{s.n_cotizaciones}</td>
                    <td className="px-5 py-3">
                      {s.proveedor_ganador_nombre ? (
                        <span className="inline-flex items-center gap-1 text-[12px] text-emerald-700">
                          <Trophy size={12} /> {s.proveedor_ganador_nombre}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3"><EstadoPill e={s.estado} /></td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/app/cotizaciones/${s.id}`}><Button variant="ghost" size="sm">Ver</Button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <NuevaSolicitudModal open={openNew} onClose={() => setOpenNew(false)}
        onCreated={() => { setOpenNew(false); qc.invalidateQueries({ queryKey: ['cot-solicitudes'] }); }} />
    </div>
  );
}

function EstadoPill({ e }: { e: string }) {
  const cfg: Record<string, string> = {
    abierta: 'bg-sky-50 text-sky-700',
    cerrada: 'bg-stone-100 text-ink-mute',
    adjudicada: 'bg-emerald-50 text-emerald-700',
    desierta: 'bg-amber-50 text-amber-700',
    cancelada: 'bg-rose-50 text-rose-700',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', cfg[e] ?? 'bg-muted')}>{e}</span>;
}

function NuevaSolicitudModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [descripcion, setDescripcion] = useState('');
  const [fechaLimite, setFechaLimite] = useState('');
  const [reqID, setReqID] = useState('');
  const [criterio, setCriterio] = useState('precio');
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: reqs } = useQuery({
    queryKey: ['requisiciones-autorizadas'], enabled: open,
    queryFn: () => api<any[]>('/requisiciones?estado=autorizada'),
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api('/cotizaciones/solicitudes', {
        method: 'POST',
        body: {
          requisicion_id: reqID || undefined,
          descripcion,
          fecha_limite: new Date(fechaLimite).toISOString(),
          criterio_evaluacion: criterio,
        },
      });
      setDescripcion(''); setFechaLimite(''); setReqID('');
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nueva solicitud de cotización"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="sol-form" loading={subm}>Crear</Button>
      </>}>
      <form id="sol-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <div>
          <label className="text-[13px] font-medium">Requisición (opcional)</label>
          <select value={reqID} onChange={(e) => setReqID(e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="">(sin requisición)</option>
            {(reqs ?? []).map((r: any) => <option key={r.id} value={r.id}>{r.folio} — {fmt.money(r.total_estimado)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[13px] font-medium">Descripción</label>
          <textarea required rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px]" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input name="fecha_limite" label="Fecha límite respuesta" type="date" required
            value={fechaLimite} onChange={(e) => setFechaLimite(e.target.value)} />
          <div>
            <label className="text-[13px] font-medium">Criterio evaluación</label>
            <select value={criterio} onChange={(e) => setCriterio(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
              <option value="precio">Precio</option>
              <option value="precio_calidad">Precio + calidad</option>
              <option value="puntos_porcentajes">Puntos y porcentajes</option>
            </select>
          </div>
        </div>
      </form>
    </Modal>
  );
}
